import { useEffect, useState } from "react";
import { useQuery, useQueryClient, useIsFetching } from "@tanstack/react-query";
import { Building2, Check, ChevronsUpDown, Info, Loader2 } from "lucide-react";
import api, { setDashboardBranch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const KEY = (uid) => `pawkt_dash_branch_${uid}`;

// Returns the branch currently applied to the dashboard ("" = all authorized branches).
export function useDashboardBranch() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [branch, setBranch] = useState(() => (user?.id && sessionStorage.getItem(KEY(user.id))) || "");
  const { data: all = [], isSuccess } = useQuery({ queryKey: ["dash-branches", user?.tenant_id, user?.id], queryFn: async () => (await api.get("/branches", { headers: { "X-Dashboard-Branch": "" } })).data });
  const scope = user?.branch_ids?.length ? new Set(user.branch_ids) : null;
  const real = all.filter((b) => !scope || scope.has(b.id));
  // business-wide users also get the records that carry no branch, as their own line (never spread across branches)
  const branches = !scope && real.length > 1 ? [...real, { id: "__unassigned__", name: "Unassigned branch" }] : real;
  const change = (b) => {
    setDashboardBranch(b);
    if (b) sessionStorage.setItem(KEY(user.id), b); else sessionStorage.removeItem(KEY(user.id));
    setBranch(b);
    qc.resetQueries({ predicate: (q) => q.queryKey[0] !== "dash-branches" });   // refetch every card together; no mixed data
  };
  useEffect(() => {
    if (isSuccess && branch && !branches.some((b) => b.id === branch)) change("");
  }, [isSuccess, branches.length, branch]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const saved = (user?.id && sessionStorage.getItem(KEY(user.id))) || "";
    setDashboardBranch(saved);
    if (saved) qc.resetQueries({ predicate: (q) => q.queryKey[0] !== "dash-branches" });
    const denied = () => change("");   // stale/unauthorized branch -> safely fall back to All Branches
    window.addEventListener("dashboard-branch-denied", denied);
    return () => {
      window.removeEventListener("dashboard-branch-denied", denied);
      // leaving the dashboard: stop narrowing and drop branch-narrowed cache so other pages never show it
      if (sessionStorage.getItem(KEY(user?.id))) { setDashboardBranch(""); qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "dash-branches" }); }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { branch, branches, change };
}

export function DashboardViewSelector({ branch, branches, onChange }) {
  const [open, setOpen] = useState(false);
  const fetching = useIsFetching();
  const real = branches.filter((b) => b.id !== "__unassigned__");
  if (real.length <= 1) {
    return real.length === 1 ? (
      <div className="flex items-center gap-2 text-sm text-pawkt-text-secondary" data-testid="dash-view-single"><Building2 className="h-4 w-4" />{real[0].name}</div>
    ) : null;
  }
  const cur = branches.find((b) => b.id === branch);
  const label = cur ? cur.name : "All Branches";
  return (
    <div className="rounded-2xl border border-pawkt-border bg-white p-3 sm:p-4" data-testid="dash-view">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-pawkt-text-secondary">Dashboard view</span>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="inline-flex min-h-[44px] w-full min-w-0 items-center justify-between gap-2 rounded-full border border-pawkt-border bg-pawkt-elevated px-4 text-sm font-semibold text-pawkt-text sm:w-auto sm:min-w-[240px]" data-testid="dash-view-trigger" aria-label="Dashboard view">
              <span className="flex min-w-0 items-center gap-2"><Building2 className="h-4 w-4 shrink-0 text-primary" /><span className="truncate" data-testid="dash-view-current">{label}</span></span>
              {fetching > 0 ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" data-testid="dash-view-loading" /> : <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(320px,calc(100vw-2rem))] p-0" align="start">
            <Command>
              {branches.length > 6 && <CommandInput placeholder="Search branches…" data-testid="dash-view-search" />}
              <CommandList>
                <CommandEmpty>No branch found.</CommandEmpty>
                <CommandGroup>
                  {[{ id: "", name: "All Branches" }, ...branches].map((b) => (
                    <CommandItem key={b.id || "all"} value={`${b.name} ${b.city || ""} ${b.id}`} onSelect={() => { onChange(b.id); setOpen(false); }} data-testid={`dash-view-opt-${b.id || "all"}`}>
                      <Check className={`mr-2 h-4 w-4 ${branch === b.id ? "opacity-100" : "opacity-0"}`} />
                      <span className="truncate">{b.name}</span>{b.city && <span className="ml-auto pl-2 text-xs text-slate-400">{b.city}</span>}
                    </CommandItem>))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cur ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700"}`} data-testid="dash-view-badge">
          {cur ? `Showing ${cur.name} only` : `Showing all ${real.length} branches combined`}</span>
      </div>
      {cur?.id === "__unassigned__" && <p className="mt-2 flex items-start gap-1.5 text-xs text-pawkt-text-secondary" data-testid="dash-view-unassigned-note"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Sales, collections, refunds, bookings and invoices recorded without a branch. They are shown here instead of being split across branches; each branch plus this line adds up to All Branches.</p>}
      {cur && cur.id !== "__unassigned__" && <p className="mt-2 flex items-start gap-1.5 text-xs text-pawkt-text-secondary" data-testid="dash-view-note"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Sales, collections, refunds, bookings, invoices, staff and stock figures are for this branch. Records not linked to any branch appear under Unassigned branch. Customer and pet totals, leads and reminders are shared across the business, so they stay business-wide.</p>}
    </div>
  );
}
